/* original source: https://ui.shadcn.com/docs/components/form */

import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
} from 'react-hook-form'
import type {
  ControllerProps,
  FieldName,
  FieldPath,
  FieldValues,
} from 'react-hook-form'
import { Alert, AlertContent, AlertDescription } from './Alert'
import { Label } from './Label'
import type { LabelProps } from './Label'
import { Slot } from './Slot'
import type { VariantProps } from '@joyful/utils'
import { cn, tv, createNonNullableContext } from '@joyful/utils'
import { useHotkey } from '@tanstack/react-hotkeys'
import { createContext, use, useId } from 'react'
import type { ComponentProps, FormEvent } from 'react'

export interface FormProps extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  onSubmit?: (event?: FormEvent<HTMLFormElement>) => void

  /**
   * @default false
   */
  shouldSubmitWithHotkey?: boolean
}

function Form({
  onSubmit,
  ref,
  shouldSubmitWithHotkey = false,
  ...props
}: FormProps) {
  useHotkey(
    'Mod+Enter',
    () => {
      void onSubmit?.()
    },
    {
      enabled: shouldSubmitWithHotkey,
      ignoreInputs: false,
      preventDefault: true,
    },
  )

  return (
    <form
      ref={ref}
      onSubmit={(event) => {
        if (onSubmit !== undefined) {
          // in case the event might bubble up to a parent form, ensure only this one submits
          event.stopPropagation()
          void onSubmit?.(event)
        }
      }}
      {...props}
    />
  )
}

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName
  size: 'sm' | 'md'
}

const FormFieldContext =
  createNonNullableContext<FormFieldContextValue>('FormFieldContext')

interface FormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
> extends ControllerProps<TFieldValues, TName, TTransformedFieldValues> {
  size?: 'sm' | 'md'
}

function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedFieldValues extends FieldValues = TFieldValues,
>({
  size = 'md',
  ...props
}: FormFieldProps<TFieldValues, TName, TTransformedFieldValues>) {
  const contextValue = {
    name: props.name,
    size,
  }

  return (
    <FormFieldContext.Context value={contextValue}>
      <Controller {...props} />
    </FormFieldContext.Context>
  )
}

function useFormField() {
  const fieldContext = FormFieldContext.useRequiredContext()
  const itemContext = FormItemContext.useRequiredContext()
  const { getFieldState } = useFormContext()
  const formState = useFormState({ name: fieldContext.name })
  const fieldState = getFieldState(fieldContext.name, formState)

  return {
    formDescriptionId: `${itemContext.id}-form-item-description`,
    formItemId: `${itemContext.id}-form-item`,
    formMessageId: `${itemContext.id}-form-item-message`,
    id: itemContext.id,
    name: fieldContext.name,
    size: fieldContext.size,
    ...fieldState,
  }
}

const AutoFilledFieldNamesContext = createContext<Set<FieldName<FieldValues>>>(
  new Set(),
)

function useIsAutoFilledFormField() {
  const field = useFormField()
  const autoFilledFieldNames = use(AutoFilledFieldNamesContext)
  return (
    autoFilledFieldNames.has(field.name) &&
    field.isTouched === false &&
    field.isDirty === false
  )
}

interface FormItemContextValue {
  id: string
}

const FormItemContext =
  createNonNullableContext<FormItemContextValue>('FormItemContext')

export const formItemVariants = tv({
  base: 'flex',
  defaultVariants: {
    orientation: 'vertical',
  },
  variants: {
    orientation: {
      horizontal: 'items-center justify-between gap-x-2',
      vertical: 'flex-col items-start gap-y-2',
    },
  },
})

export type FormItemProps = ComponentProps<'div'> &
  VariantProps<typeof formItemVariants>

function FormItem({ className, orientation, ...props }: FormItemProps) {
  const id = useId()

  const contextValue = {
    id,
  }

  return (
    <FormItemContext.Context value={contextValue}>
      <div
        data-slot="form-item"
        className={cn(
          formItemVariants({
            orientation,
          }),
          className,
        )}
        {...props}
      />
    </FormItemContext.Context>
  )
}

function FormLabel(props: LabelProps) {
  const { error, formItemId } = useFormField()

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      htmlFor={formItemId}
      {...props}
    />
  )
}

function FormControl(props: ComponentProps<typeof Slot>) {
  const { error, formDescriptionId, formItemId, formMessageId } = useFormField()
  const isAutoFilled = useIsAutoFilledFormField()

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      data-autofilled={isAutoFilled}
      {...props}
    />
  )
}

export function RawFormDescription({
  className,
  ...props
}: ComponentProps<'p'>) {
  return (
    <p
      data-slot="form-description"
      className={cn('text-tremor-content text-xs text-balance', className)}
      {...props}
    />
  )
}

function FormDescription(props: ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField()

  return <RawFormDescription id={formDescriptionId} {...props} />
}

function FormErrorMessage({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      data-slot="form-message"
      className={cn('shrink-0 text-xs text-red-500', className)}
      {...props}
    />
  )
}

function FormMessage(props: ComponentProps<'p'>) {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message ?? '') : props.children

  if (!body) {
    return null
  }

  return (
    <FormErrorMessage id={formMessageId} {...props}>
      {body}
    </FormErrorMessage>
  )
}

function FormRootErrorAlert() {
  const form = useFormContext()

  // [rhf bug](https://github.com/colinhacks/zod/issues/3919)
  // SAFETY: react-hook-form stores Zod root errors on the empty-string key, not `.root`.
  const rootError = form.formState.errors[
    ''
  ] as typeof form.formState.errors.root

  if (!rootError?.message) {
    return null
  }

  return (
    <div className="animate-in slide-in-from-top">
      <Alert variant="error">
        <AlertContent>
          <AlertDescription>{rootError.message}</AlertDescription>
        </AlertContent>
      </Alert>
    </div>
  )
}

export type FieldsetProps = ComponentProps<'fieldset'>

function Fieldset({ children, className, ...props }: FieldsetProps) {
  return (
    <fieldset
      className={cn('flex min-w-0 flex-col gap-4', className)}
      {...props}
    >
      {children}
    </fieldset>
  )
}

function FieldsetHeader({
  children,
  className,
  ...props
}: ComponentProps<'h3'>) {
  return (
    <h3
      className={cn(
        'text-tremor-content-emphasis text-sm font-semibold',
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  )
}

export {
  AutoFilledFieldNamesContext,
  useIsAutoFilledFormField,
  Form,
  FormProvider,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormErrorMessage,
  FormRootErrorAlert,
  FormMessage,
  FormField,
  Fieldset,
  FieldsetHeader,
}
